import { useEffect } from 'react'
import { getWaveEnd } from './motionClock'

const CARD = '.ant-card'
const ROW = '.ant-table-tbody > tr:not(.ant-table-measure-row), .ant-list-items > .ant-list-item'

/** Cards across a row start this far apart; the seventh and later together. */
const CARD_GAP_MS = 90
const CARD_MAX_INDEX = 6
/** Cards start this long before the wave ends: as it is about half gone. */
const CARD_WAVE_LEAD_MS = 500
/** A card's rows start this long after the card does: once it's mostly in. */
const ROWS_AFTER_CARD_MS = 320
/** Rows start this long before the wave ends: as it clears. */
const ROW_WAVE_LEAD_MS = 300
/** Rows start this far apart; the twelfth and later together. */
const ROW_GAP_MS = 80
const ROW_MAX_INDEX = 11

/**
 * The portal's two staggers and its scroll reveal, as on the website. Cards
 * rise in one after another across each row; the rows inside them (table
 * rows, list items) follow their card in, one after another; cards and rows
 * below the fold wait out of sight and rise in as they scroll into view.
 *
 * Delays are worked out when each element appears, not fixed in CSS, because
 * rows usually arrive later than the page, with their data: rows that come
 * after the wave and their card cascade at once, with no dead wait. The CSS
 * (motion.css) reads `--fh-delay` (entrance) and `--fh-col-delay` (a card's
 * place in its row, for the scroll reveal).
 *
 * Works on every page without touching it: it watches the content column.
 * Off for reduced motion.
 */
export function PageMotion() {
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const content = document.querySelector('.fh-content')
    if (!content) return

    // When each card is mostly in, so its rows can follow it.
    const cardMostlyInAt = new WeakMap<Element, number>()

    const reveal =
      typeof IntersectionObserver === 'undefined'
        ? null
        : new IntersectionObserver(
            (entries) => {
              for (const entry of entries) {
                if (!entry.isIntersecting) continue
                entry.target.setAttribute('data-fh-reveal', 'shown')
                reveal?.unobserve(entry.target)
              }
            },
            { rootMargin: '0px 0px -8% 0px', threshold: 0.1 },
          )

    // Rows below the fold outside a waiting card (a table on the page itself,
    // like Users under its stats) wait too, then cascade in as they scroll
    // into view, in the order they arrive.
    const rowReveal =
      typeof IntersectionObserver === 'undefined'
        ? null
        : new IntersectionObserver(
            (entries) => {
              const arrived = entries
                .filter((e) => e.isIntersecting)
                .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
              arrived.forEach((entry, i) => {
                const row = entry.target as HTMLElement
                row.style.setProperty('--fh-delay', `${Math.min(i, ROW_MAX_INDEX) * ROW_GAP_MS}ms`)
                row.setAttribute('data-fh-reveal-row', 'shown')
                rowReveal?.unobserve(row)
              })
            },
            { rootMargin: '0px 0px -6% 0px', threshold: 0.2 },
          )

    const setCard = (card: HTMLElement, now: number) => {
      if (card.dataset.fhMotion) return
      card.dataset.fhMotion = 'card'
      // A card inside a card moves with its parent.
      if (card.parentElement?.closest(CARD)) return

      const col = card.parentElement?.classList.contains('ant-col') ? card.parentElement : null
      const index = col ? Math.min([...col.parentElement!.children].indexOf(col), CARD_MAX_INDEX) : 0
      const colDelay = index * CARD_GAP_MS
      card.style.setProperty('--fh-col-delay', `${colDelay}ms`)

      if (reveal && card.getBoundingClientRect().top >= window.innerHeight) {
        card.setAttribute('data-fh-reveal', 'waiting')
        reveal.observe(card)
        return
      }
      const wait = Math.max(0, getWaveEnd() - CARD_WAVE_LEAD_MS - now)
      card.style.setProperty('--fh-delay', `${wait + colDelay}ms`)
      cardMostlyInAt.set(card, now + wait + colDelay + ROWS_AFTER_CARD_MS)
    }

    const setRow = (row: HTMLElement, now: number) => {
      if (row.dataset.fhMotion) return
      row.dataset.fhMotion = 'row'
      const siblings = [...row.parentElement!.children].filter((el) => el.matches(ROW))
      const index = Math.min(siblings.indexOf(row), ROW_MAX_INDEX)
      const card = row.closest<HTMLElement>(CARD)

      // In a card still waiting below the fold: the rows are paused (motion.css)
      // until it's revealed, so count from the reveal.
      if (card?.getAttribute('data-fh-reveal') === 'waiting') {
        const colDelay = parseFloat(card.style.getPropertyValue('--fh-col-delay') || '0')
        row.style.setProperty('--fh-delay', `${colDelay + ROWS_AFTER_CARD_MS + index * ROW_GAP_MS}ms`)
        return
      }
      if (rowReveal && row.getBoundingClientRect().top >= window.innerHeight) {
        row.setAttribute('data-fh-reveal-row', 'waiting')
        rowReveal.observe(row)
        return
      }
      const mostlyIn = card ? cardMostlyInAt.get(card) : undefined
      const base = Math.max(0, getWaveEnd() - ROW_WAVE_LEAD_MS - now, mostlyIn ? mostlyIn - now : 0)
      row.style.setProperty('--fh-delay', `${base + index * ROW_GAP_MS}ms`)
    }

    const scan = (root: Element) => {
      const now = performance.now()
      const cards = root.matches(CARD) ? [root] : []
      cards.push(...root.querySelectorAll(CARD))
      cards.forEach((card) => setCard(card as HTMLElement, now))
      const rows = root.matches(ROW) ? [root] : []
      rows.push(...root.querySelectorAll(ROW))
      rows.forEach((row) => setRow(row as HTMLElement, now))
    }

    scan(content)
    const mutations = new MutationObserver((records) => {
      for (const record of records) {
        record.addedNodes.forEach((node) => {
          if (node instanceof Element) scan(node)
        })
      }
    })
    mutations.observe(content, { childList: true, subtree: true })

    return () => {
      mutations.disconnect()
      reveal?.disconnect()
      rowReveal?.disconnect()
    }
  }, [])

  return null
}
